// PATCH /api/projects/:id/issues/:iid — used by the IssueEditor's edit
// path in both Phase 2c (fields-only) and Phase 2d (methodology-aware).
// The wire shape is `Partial<IssueEditorPayload>` so callers can patch
// just what they touched (`diffDraftPatch`, ./issueDraft.ts, builds it).

import { useCallback, useRef, useState } from "react";
import { z } from "zod";

import {
  DashboardIssueSchema,
  type DashboardIssue,
  type IssueEditorPayload,
} from "@loom/shared/dashboard";

import { httpPatch } from "../lib/http";

const UpdateResponseSchema = z.object({
  issue: DashboardIssueSchema,
});

/**
 * What a Save sends. Every member is optional, with ONE invariant the type
 * cannot express: the body trio — `description`, `outcome`,
 * `acceptanceCriteria` — travels together or not at all. The server rebuilds
 * the body from the three whenever any of them arrives and defaults a missing
 * one to "" (`parsed.data.outcome ?? ""` in the PATCH route), so a patch
 * carrying one body field alone blanks the other two sections on GitLab.
 */
export type IssuePatch = Partial<IssueEditorPayload>;

export interface UpdateIssueState {
  readonly update: (iid: number, patch: IssuePatch) => Promise<DashboardIssue>;
  readonly saving: boolean;
  readonly error: string | null;
}

export const useUpdateIssue = (projectId: number): UpdateIssueState => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);

  const update = useCallback(
    async (iid: number, patch: IssuePatch): Promise<DashboardIssue> => {
      if (inflight.current) {
        throw new Error("update already in flight");
      }
      inflight.current = true;
      setSaving(true);
      setError(null);
      try {
        const res = await httpPatch(
          `/api/projects/${projectId}/issues/${iid}`,
          patch,
          UpdateResponseSchema,
        );
        return res.issue;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        inflight.current = false;
        setSaving(false);
      }
    },
    [projectId],
  );

  return { update, saving, error };
};
